import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getBlogCache, setBlogCache } from '../lib/cache';
import type { BlogPost } from '../types/blog';

export function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPosts() {
      try {
        // Check cache first
        const cachedPosts = getBlogCache();
        if (cachedPosts) {
          setPosts(cachedPosts);
          setLoading(false);
          
          // Fetch in background to update cache
          fetchFromApi();
          return;
        }

        await fetchFromApi();
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError('Failed to load blog posts');
        setLoading(false);
      }
    }

    async function fetchFromApi() {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          category:blog_categories(name, slug)
        `)
        .eq('status', 'published')
        .is('deleted_at', null)
        .order('published_at', { ascending: false });

      if (error) throw error;
      
      setPosts(data || []);
      setBlogCache(data || []);
      setLoading(false);
    }

    fetchPosts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[--bg-secondary] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-[--bg-primary] shadow rounded-lg p-6">
                <div className="h-8 bg-[--bg-tertiary] rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-[--bg-tertiary] rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-[--bg-tertiary] rounded w-1/4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[--bg-secondary] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-error-50 dark:bg-[rgb(var(--error-900)/0.2)] p-4 rounded-lg">
            <p className="text-error-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[--bg-secondary] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[--text-primary]">
            Under Construction
          </h1>
          <p className="mt-2 text-base text-[--text-secondary]">
            Stay updated with our product developments
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="bg-[--bg-primary] shadow-xl rounded-lg overflow-hidden transform hover:scale-[1.02] transition-transform duration-300"
            >
              {post.featured_image && (
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-6 flex flex-col h-full">
                <div className="flex items-center space-x-2 text-sm text-[--text-secondary] mb-3">
                  <time dateTime={post.published_at || post.created_at}>
                    {format(new Date(post.published_at || post.created_at), 'MMMM d, yyyy')}
                  </time>
                  {post.category && (
                    <>
                      <span>•</span>
                      <span>{post.category.name}</span>
                    </>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-[--text-primary] mb-3">
                  {post.title}
                </h3>
                
                {post.summary && (
                  <p className="text-[--text-secondary] mb-4 line-clamp-2">
                    {post.summary}
                  </p>
                )}
                
                <div className="mt-auto flex items-end justify-between">
                  <span className="text-sm text-[--text-primary]">
                    By {post.author_name}
                  </span>
                  
                  <span className="text-xs text-[--link-default] hover:text-[--link-hover] flex items-center">
                    Read more
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {posts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[--text-secondary]">
              No posts found.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}